export const GlobalStyles = {
  // Layout
  pageContainer: `min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800 transition-all duration-500`,
  contentContainer: `w-full max-w-full p-4 md:p-6 lg:p-8`,

  // Cards
  card: `bg-white dark:bg-gray-900/90 backdrop-blur-lg rounded-2xl border border-gray-100 dark:border-gray-800 shadow-lg p-6 transition-all duration-400 hover:shadow-xl`,
  cardHover: `bg-white dark:bg-gray-900/90 backdrop-blur-lg rounded-2xl border border-gray-100 dark:border-gray-800 shadow-lg p-6 transition-all duration-400 hover:shadow-2xl hover:-translate-y-1 hover:border-primary/30 dark:hover:border-primary/40`,

  // Buttons
  button: `flex p-2 items-center justify-center font-medium rounded-xl transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-3 focus:ring-primary/20 active:scale-[0.97]`,

  buttonPrimary: `bg-gradient-to-br from-primary-600 via-primary-500 to-primary-600 text-white shadow-lg hover:shadow-xl hover:from-primary-500 hover:via-primary-400 hover:to-primary-500`,
  buttonOutline: `border-2 border-primary-500/30 text-primary-600 dark:text-primary-400 bg-transparent hover:bg-primary/5 dark:hover:bg-primary/10 hover:border-primary-500/50`,
  buttonGhost: `text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/70 dark:hover:bg-gray-800/70`,
  buttonDanger: `bg-gradient-to-br from-red-500 via-red-400 to-red-500 text-white shadow-lg hover:shadow-xl`,
  buttonSuccess: `bg-gradient-to-br from-emerald-500 via-emerald-400 to-emerald-500 text-white shadow-lg hover:shadow-xl`,
  buttonError: `bg-gradient-to-br from-rose-500 via-rose-400 to-rose-500 text-white shadow-lg hover:shadow-xl`,

  buttonSmall: "px-4 py-2 text-sm font-semibold",
  buttonMedium: "px-5 py-3 text-base font-semibold",
  buttonLarge: "px-7 py-4 text-lg font-semibold",

  // Inputs
  input: `w-full bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-3 focus:ring-primary/15 focus:border-primary/50 transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-500/60 dark:placeholder:text-gray-400/60 text-sm`,
  inputError: "border-rose-500 focus:ring-rose-500/15 focus:border-rose-500",
  inputLabel: `font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300`,

  // Text
  heading1: "text-4xl font-bold tracking-tight text-gray-900 dark:text-white",
  heading2: "text-2xl font-bold text-gray-900 dark:text-white",
  heading3: "text-xl font-semibold text-gray-900 dark:text-white",
  bodyText: "text-gray-700 dark:text-gray-300 text-base leading-relaxed",
  smallText: "text-sm text-gray-500 dark:text-gray-400",

  // Badges
  badge: `rounded-full inline-flex items-center justify-center px-3 py-1 text-xs font-semibold backdrop-blur-sm border`,
  badgeSuccess:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50",
  badgeWarning:
    "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/50",
  badgeError:
    "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/50",
  badgeInfo:
    "bg-primary/10 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800/50",
  badgePrimary:
    "bg-primary/15 text-primary-700 dark:text-primary-200 border-primary/30 dark:border-primary/40",

  // Tables
  table: `w-full border-collapse rounded-xl overflow-hidden text-sm`,
  tableHeader: `bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 font-semibold text-gray-700 dark:text-gray-300`,
  tableRow:
    "border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-all duration-200",
  tableCell: "px-5 py-4",

  // Alerts
  alert: `rounded-xl p-4 flex items-start gap-3 backdrop-blur-sm border-2`,
  alertInfo:
    "bg-primary/5 text-primary-700 dark:text-primary-300 border-primary/20 dark:border-primary/30",
  alertSuccess:
    "bg-emerald-500/5 text-emerald-700 dark:text-emerald-300 border-emerald/20 dark:border-emerald/30",
  alertWarning:
    "bg-amber-500/5 text-amber-700 dark:text-amber-300 border-amber/20 dark:border-amber/30",
  alertError:
    "bg-rose-500/5 text-rose-700 dark:text-rose-300 border-rose/20 dark:border-rose/30",

  // Loaders
  spinner:
    "animate-spin rounded-full border-[3px] border-dashed border-primary/30 border-t-primary",

  // Grid
  gridCols1: "grid grid-cols-1 gap-5",
  gridCols2: "grid grid-cols-1 md:grid-cols-2 gap-5",
  gridCols3: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5",
  gridCols4: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5",

  // Spacing
  sectionSpacing: "mb-8",
  cardSpacing: "space-y-5",

  // Misc
  iconButton:
    "p-2.5 rounded-xl hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-all duration-200 active:scale-95",

  // Chat specific styles (modernized)
  chatMessageOwn:
    "bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-2xl rounded-br-none shadow-lg",
  chatMessageOther:
    "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl rounded-bl-none shadow",
  chatBubble: "px-4 py-3 max-w-[80%] text-sm",
  chatTime: "text-xs opacity-70 mt-2",
  chatAttachment:
    "mt-3 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow",

  // Modal styles
  modalBackdrop:
    "fixed inset-0 bg-black/70 backdrop-blur-md z-50 transition-all duration-400",
  modalContainer:
    "fixed z-50 bg-white dark:bg-gray-900 shadow-2xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50",

  // Input styles for chat
  chatInput:
    "min-h-[48px] max-h-[120px] resize-none pr-14 border-2 border-gray-200 dark:border-gray-800 focus:border-primary/50 transition-all duration-300 rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm text-base",

  // Button styles for chat
  chatButton:
    "p-2.5 rounded-xl hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-all duration-200 active:scale-95",
  chatSendButton:
    "p-3 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white shadow-lg hover:shadow-xl transition-all duration-300",

  // Badge styles
  badgeNotification:
    "absolute -top-1 -right-1 bg-gradient-to-br from-rose-500 to-rose-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900",

  // New modern additions
  glassPanel: `bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/20 dark:border-gray-800/50 rounded-2xl shadow-lg`,
  gradientBorder: `relative bg-gradient-to-br from-primary/20 via-transparent to-primary/20 p-[1px] rounded-2xl`,
  gradientBorderContent: `bg-white dark:bg-gray-900 rounded-2xl p-6`,

  // Tabs
  tabActive: `bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-lg`,
  tabInactive: `text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 font-medium`,

  // Progress
  progressBar: `h-2 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden`,
  progressFill: `h-full bg-gradient-to-r from-primary-400 to-primary-500 rounded-full transition-all duration-500`,

  // Avatar
  avatar: `rounded-full border-2 border-white dark:border-gray-900 shadow`,
  avatarGroup: `flex -space-x-3`,

  // Stats cards
  statCard: `bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-800`,
  statValue: `text-3xl font-bold text-gray-900 dark:text-white`,
  statLabel: `text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide`,

  // Navigation
  navItem: `px-4 py-2.5 rounded-xl text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-all duration-200`,
  navItemActive: `px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 text-primary-600 dark:text-primary-400 border border-primary/20 dark:border-primary/30`,

  // Dividers
  divider: `my-6 border-t border-gray-200 dark:border-gray-800`,
  dividerWithText: `flex items-center my-6 text-gray-500 dark:text-gray-400 text-sm before:content-[''] before:flex-1 before:border-t before:border-gray-200 dark:before:border-gray-800 before:mr-3 after:content-[''] after:flex-1 after:border-t after:border-gray-200 dark:after:border-gray-800 after:ml-3`,
};
